import { useEffect, useMemo, useRef, useState } from "react"
import { Trash2, PackageSearch } from "lucide-react"
import {
  useGetItemsQuery,
  useLazyGetItemStockQuery,
  useCreateItemMutation,
  useGetItemGroupsQuery,
  useLazyGetProcurementSupplierItemPricesQuery,
} from "shared/redux/endpoints/procurementItems"
import { useGetPurchaseSuppliersQuery } from "shared/redux/endpoints/procurementPurchases"
import {
  useGetCenterRepresentativeQuery,
  useGetCenterAddressQuery,
  useLazyGetCenterAddressQuery,
} from "shared/redux/endpoints/procurementDirectory"
import { ITEM_TYPE_OPTIONS, CUSTOM_ITEM_VALUE } from "../constants"
import MDSelect from "components/MDSelect"
import { Button, Currency, Input, NumberControl, NumberInput, Select, FileSlot, StatusBadge, Checkbox } from "../ui"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { getApiErrorMessage } from "../orderSubmitFeedback"
import { RULES, validateFields, hasErrors } from "../fieldRules"

// Sentinel for "+ Create a new catalog item…" — only offered when the caller
// passes `categoryId` (DirectSourceBuilder; the Order Request form doesn't,
// it has off-catalog for the same job). Distinct from CUSTOM_ITEM_VALUE:
// this creates a REAL item_master row (via POST /items, flagged with
// whatever mustHaveOrder/mustHavePurchase this row was scoped by) instead of
// a free-text off-catalog line — for when the spend should land in the
// catalog for reuse, not just get sourced once.
const CREATE_ITEM_VALUE = "__create_item__"

const emptyNewItemForm = { itemCode: "", name: "", itemGroupId: "", uom: "", defaultVendorId: "", standardPrice: "" }

// Responsive field grid, same convention as the rest of the Order Request
// form — collapses to one column on mobile.
const LineGrid = ({ children, min = 180 }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
      gap: 16,
    }}
  >
    {children}
  </div>
)

// One Order Request line item: item (find-all from the item catalog, scoped
// to the order header's selected Group Items — center_id doesn't filter the
// list, it only annotates each item with `isOrderable` for this line's
// center), center (scoped to the order's region), type (new / replacement),
// quantity, unit price, per-line delivery contact, and a "Check Stock"
// lookup. Kept as its own component (rather than inlined in a .map() in the
// parent) so each row can independently own its item query (annotation
// depends on its own center) and "Check Stock" lazy query.
//
// `categoryId`/`mustHaveOrder`/`mustHavePurchase` (all optional, default
// undefined — no behavior change for the Order Request form, which only ever
// passes `groupItemId`): lets DirectSourceBuilder reuse this same row to pick
// items scoped by budget category + the barrier-removal flags instead of an
// item group (2026-09 feature — see DirectSourceBuilder.js).
// `showDeliveryContact`/`showProofImage` (default true): DirectSourceBuilder
// turns both off — delivery contact doesn't fit a subscription-style line,
// and image-proof needs an upload-first endpoint this feature doesn't have.
const OrderLineRow = ({
  line,
  idx,
  centerOptions,
  groupItemId,
  // Order Request form: each line picks its own Group Item from `groupItems`
  // (the order Category's groups, raw rows) — it scopes that line's item list
  // and isn't sent to the BE. Without `groupItems` the `groupItemId` prop
  // scopes the list instead.
  groupItems,
  // Items only load once a Group Item is picked.
  requireGroupItem = false,
  categoryId,
  mustHaveOrder,
  mustHavePurchase,
  showDeliveryContact = true,
  showProofImage = true,
  // Multiple-months orders: contract-price catalog items only, no off-catalog.
  referencePriceOnly = false,
  // Multiple-months order: [{ key, label, quantity, unitPrice, amount }] — this
  // card's item is sent as one OR line per month; qty/price above apply to all.
  monthBreakdown = null,
  // (idx, month key, qty): overrides one month; the card's qty resets them all.
  onMonthQuantity,
  onUpdate,
  onRemove,
  canRemove,
  onStageProofImage,
  disabled,
  // Over-budget center: BE rejects submit unless every line has note + ticketLink.
  overBudget = false,
  // { [ui field]: message } from validateOrderLine, shown once the parent tried to save.
  errors = {},
}) => {
  const effectiveGroupItemId = groupItems ? line.groupItemId : groupItemId
  const needsGroupItem = requireGroupItem && !effectiveGroupItemId
  const lineGroupItem = groupItems?.find((g) => String(g.id) === String(line.groupItemId))
  const lineGroupItemLCodes = lineGroupItem
    ? Array.from({ length: 10 }, (_, i) => lineGroupItem[`l${i + 1}Code`])
        .map((code, i) => (code ? `L${i + 1}: ${code}` : null))
        .filter(Boolean)
    : []
  const { data: items, isFetching: itemsLoading } = useGetItemsQuery(
    {
      centerId: line.centerId || undefined,
      itemGroupId: effectiveGroupItemId || undefined,
      categoryId: categoryId || undefined,
      mustHaveOrder,
      mustHavePurchase,
      isApplyReferencePrice: referencePriceOnly || undefined,
      // The list is paginated server-side; load it all so the picker can
      // search/scroll the full catalog for this scope.
      size: 10000,
    },
    { skip: needsGroupItem }
  )
  const [checkStock, stockResult] = useLazyGetItemStockQuery()
  // A4 — when a chosen item has a default vendor, pull that vendor's own price
  // for the item and use it (editable), falling back to the item's standard
  // price. Lazy so it only fires on an actual item pick.
  const [fetchVendorPrices] = useLazyGetProcurementSupplierItemPricesQuery()

  // A5 — the picked center's representative becomes the line's delivery
  // contact. Mock until GET /centers carries a representative (see
  // procurementDirectory.js). Prefill only empty fields so a manual override
  // or a saved value is never clobbered.
  const { data: centerRep } = useGetCenterRepresentativeQuery(line.centerId || undefined, {
    skip: !line.centerId || !showDeliveryContact,
  })

  // Prefills a line that starts with a center already set (new line with the
  // default center). Picking a center fetches the address directly in
  // handleCenterChange instead.
  const { currentData: centerAddress } = useGetCenterAddressQuery(line.centerId || undefined, {
    skip: !line.centerId || !showDeliveryContact,
  })
  const [fetchCenterAddress] = useLazyGetCenterAddressQuery()
  const latestCenterRef = useRef(line.centerId)

  // ── Inline "+ Create a new catalog item" (only offered when `categoryId`
  // is set — see CREATE_ITEM_VALUE above) ────────────────────────────────────
  const [creatingNewItem, setCreatingNewItem] = useState(false)
  const [newItemForm, setNewItemForm] = useState(emptyNewItemForm)
  const [newItemErrors, setNewItemErrors] = useState({})
  const setNewItemField = (k, v) => {
    setNewItemForm((p) => ({ ...p, [k]: v }))
    setNewItemErrors((p) => ({ ...p, [k]: null }))
  }
  const { data: newItemGroups } = useGetItemGroupsQuery(
    { categoryId: categoryId || undefined },
    { skip: !creatingNewItem || !categoryId }
  )
  const newItemGroupOptions = useMemo(
    () => (newItemGroups || []).map((g) => ({ value: g.id, label: g.name || g.code })),
    [newItemGroups]
  )
  const { data: newItemVendors } = useGetPurchaseSuppliersQuery(undefined, { skip: !creatingNewItem })
  const newItemVendorOptions = useMemo(
    () => (newItemVendors || []).map((s) => ({ value: s.id, label: s.name })),
    [newItemVendors]
  )
  const [createItem, { isLoading: creatingItemSaving }] = useCreateItemMutation()

  // isOrderable is true/false when item_center_availability has a row for
  // this item+center, null when no row exists yet (or no center is picked) —
  // only `false` blocks picking it; grayed out via the native `disabled`
  // option attribute rather than removed from the list. The trailing sentinel
  // option lets a requester name something that isn't in the catalog at all
  // instead of being blocked on it (see handleItemChange) — treated as an
  // "off-catalog" line: no stock lookup, no vendor-proposal line item, and
  // the order can still be submitted/approved without a vendor picked for it.
  // "+ Add an item not in this list…" (and, in the direct-spend builder,
  // "+ Create a new catalog item…") go FIRST, not after the catalog — with a
  // long catalog, a native <select> makes you scroll past every item to
  // reach them at the bottom otherwise.
  const itemOptions = useMemo(
    () => [
      ...(referencePriceOnly ? [] : [{ value: CUSTOM_ITEM_VALUE, label: "+ Add an item not in this list…" }]),
      ...(categoryId ? [{ value: CREATE_ITEM_VALUE, label: "+ Create a new catalog item…" }] : []),
      ...(items || []).map((it) => {
        const name = it.name || it.itemCode
        return {
          value: it.id,
          label: it.isOrderable === false ? `${name} (not orderable at this center)` : name,
          disabled: it.isOrderable === false,
          raw: it,
        }
      }),
    ],
    [items, categoryId, referencePriceOnly]
  )

  // Estimate-price item (isApplyReferencePrice=false): its standard price is
  // only a guess until suppliers quote. Read from the picked catalog item,
  // else from the saved line (the BE line response carries the flag).
  const pickedItem = itemOptions.find((o) => String(o.value) === String(line.itemId))?.raw
  const isEstimatePrice =
    !line.isCustomItem && !!line.itemId && (pickedItem?.isApplyReferencePrice ?? line.isApplyReferencePrice) === false

  const handleCenterChange = (v) => {
    // Center only tags this line for delivery/stock lookup — the chosen
    // item isn't scoped by center, so it doesn't need to be cleared here.
    // Any stock reading is for the old center though, so that resets.
    onUpdate(idx, { centerId: v || "", stockReading: null, deliveryAddress: "" })
    latestCenterRef.current = v
    if (!v || !showDeliveryContact) return
    // GET /private/center/v1?centerId=…; ignore a slow reply for a center the
    // user has already switched away from.
    fetchCenterAddress(v, true)
      .unwrap()
      .then((address) => {
        if (address && latestCenterRef.current === v) onUpdate(idx, { deliveryAddress: address })
      })
      .catch(() => {})
  }

  // A5 — prefill delivery contact from the center's representative, only into
  // still-empty fields so a manual edit / saved value is kept.
  useEffect(() => {
    if (!centerRep) return
    const patch = {}
    if (!line.deliveryContactName && centerRep.name) patch.deliveryContactName = centerRep.name
    if (!line.deliveryContactPhone && centerRep.phone) patch.deliveryContactPhone = centerRep.phone
    if (Object.keys(patch).length) onUpdate(idx, patch)
    // Keyed on the resolved rep + center only — re-running on every field
    // change would fight the user's own edits.
  }, [centerRep, line.centerId]) // eslint-disable-line

  useEffect(() => {
    if (centerAddress && !line.deliveryAddress) onUpdate(idx, { deliveryAddress: centerAddress })
  }, [centerAddress, line.centerId]) // eslint-disable-line

  const handleItemChange = (v) => {
    if (v === CUSTOM_ITEM_VALUE) {
      onUpdate(idx, {
        itemId: null,
        itemName: "",
        isCustomItem: true,
        customItemName: "",
        stockReading: null,
      })
      return
    }
    if (v === CREATE_ITEM_VALUE) {
      setNewItemForm(emptyNewItemForm)
      setCreatingNewItem(true)
      return
    }
    const opt = itemOptions.find((o) => String(o.value) === String(v))
    const item = opt?.raw
    onUpdate(idx, {
      itemId: opt?.value ?? null,
      itemName: opt?.label || "",
      isCustomItem: false,
      customItemName: "",
      isApplyReferencePrice: item?.isApplyReferencePrice,
      // Standard price first; the default-vendor price (if any) overrides it
      // below once fetched. Either way the field stays editable.
      unitPrice: item?.standardPrice ?? line.unitPrice,
      stockReading: null,
    })
    // A4 — override with the item's default vendor's price when available.
    // Only for contract-price items; an estimate-price item's standardPrice is
    // just a guess until suppliers quote.
    if (item?.id && item?.defaultVendorId && item?.isApplyReferencePrice !== false) {
      fetchVendorPrices({ supplierId: item.defaultVendorId, itemIds: [item.id] })
        .unwrap()
        .then((prices) => {
          const match = (prices || []).find((p) => String(p.itemId) === String(item.id))
          const vendorPrice = match?.price ?? match?.unitPrice ?? match?.proposedAmount
          if (vendorPrice != null) onUpdate(idx, { unitPrice: vendorPrice })
        })
        .catch(() => {})
    }
  }

  const handleBackToCatalog = () =>
    onUpdate(idx, { isCustomItem: false, customItemName: "", itemId: null, itemName: "" })

  const isNewItemFormValid =
    newItemForm.itemCode.trim() &&
    newItemForm.name.trim() &&
    newItemForm.itemGroupId &&
    newItemForm.uom.trim() &&
    newItemForm.defaultVendorId &&
    newItemForm.standardPrice !== "" &&
    Number(newItemForm.standardPrice) >= 0

  const handleSaveNewItem = async () => {
    if (!isNewItemFormValid) return
    const errs = validateFields(newItemForm, RULES.item)
    setNewItemErrors(errs)
    if (hasErrors(errs)) return
    try {
      const created = await createItem({
        itemCode: newItemForm.itemCode.trim(),
        name: newItemForm.name.trim(),
        itemGroupId: newItemForm.itemGroupId,
        uom: newItemForm.uom.trim(),
        defaultVendorId: newItemForm.defaultVendorId,
        standardPrice: Number(newItemForm.standardPrice) || 0,
        // Preset from this row's own scope so the item is immediately usable
        // where it was just created for — same idea as an inline "+ Create
        // new category" defaulting its flags from the current mode.
        mustHaveOrder: mustHaveOrder === false ? false : undefined,
        mustHavePurchase: mustHavePurchase === false ? false : undefined,
      }).unwrap()
      showToastSuccess("Item created.")
      setCreatingNewItem(false)
      onUpdate(idx, {
        itemId: created.id,
        itemName: created.name,
        isCustomItem: false,
        customItemName: "",
        unitPrice: created.standardPrice ?? newItemForm.standardPrice,
        stockReading: null,
      })
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Couldn't create this item. Please try again."))
    }
  }

  const handleCancelNewItem = () => {
    setCreatingNewItem(false)
    setNewItemForm(emptyNewItemForm)
  }

  const handleProofImageSelect = (file) => {
    const labeledName = `Line ${idx + 1}${
      line.itemName || line.customItemName ? ` - ${line.itemName || line.customItemName}` : ""
    } - ${file.name}`
    const labeled = new File([file], labeledName, { type: file.type })
    onUpdate(idx, { proofImage: labeled })
    onStageProofImage?.(labeled)
  }

  const handleCheckStock = async () => {
    if (!line.itemId || !line.centerId) return
    const res = await checkStock({ itemId: line.itemId, centerId: line.centerId })
    // 404 means no stock row for this item/center — that's "0 on hand", not
    // a failed lookup, so it must still render (not fall through the `!=
    // null` check below and look like the button did nothing). 403 is a real
    // permission gap — surface it instead of silently doing nothing.
    if (res?.error) {
      if (res.error.status === 404) {
        onUpdate(idx, { stockReading: 0 })
      } else if (res.error.status === 403) {
        onUpdate(idx, { stockReading: null })
        showToastError("You don't have permission to view stock.")
      } else {
        onUpdate(idx, { stockReading: null })
      }
      return
    }
    const qty = res?.data?.quantity ?? res?.data?.quantityOnHand ?? 0
    onUpdate(idx, { stockReading: qty })
  }

  const amount = monthBreakdown
    ? monthBreakdown.reduce((s, m) => s + m.amount, 0)
    : (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0)

  return (
    <div className="pc-line">
      <div className="pc-line-head">
        <div className="pc-line-head-l">
          <span className="pc-line-no">Line {idx + 1}</span>
          {line.status && <StatusBadge status={line.status} />}
          {line.isCustomItem && <span className="pc-badge pc-badge--warn">Off-catalog item</span>}
          {(line.itemName || line.customItemName) && (
            <span className="pc-line-name" title={line.itemName || line.customItemName}>
              {line.itemName || line.customItemName}
            </span>
          )}
        </div>
        <div className="pc-line-total">
          <span className="pc-line-total-label">Line total</span>
          <Currency value={amount} strong />
        </div>
      </div>

      <div className="pc-line-row">
        <div className="pc-lf">
          <Select
            label="Center"
            required
            value={line.centerId}
            onChange={handleCenterChange}
            options={centerOptions}
            disabled={disabled || centerOptions.length === 0}
            placeholder="Select center…"
          />
        </div>
        {groupItems && !line.isCustomItem && (
          <div className="pc-lf pc-lf--md">
            <Select
              label="Group Item"
              required={requireGroupItem}
              value={line.groupItemId || ""}
              // A different group has a different item list — drop the picked item.
              onChange={(v) => onUpdate(idx, { groupItemId: v, itemId: null, itemName: "", stockReading: null })}
              options={groupItems.map((g) => ({ value: g.id, label: g.name || g.code }))}
              disabled={disabled || groupItems.length === 0}
              placeholder={groupItems.length === 0 ? "Chọn Category trước" : "Select group item…"}
              hint={
                lineGroupItem && (lineGroupItem.accountCode || lineGroupItemLCodes.length > 0)
                  ? [lineGroupItem.accountCode && `Account code: ${lineGroupItem.accountCode}`, lineGroupItemLCodes.join(" · ")]
                      .filter(Boolean)
                      .join(" — ")
                  : undefined
              }
            />
          </div>
        )}
        <div className="pc-lf pc-lf--lg">
          {line.isCustomItem ? (
            <div>
              <Input
                label="Item Name"
                required
                placeholder="Describe the item you need"
                value={line.customItemName}
                error={errors.customItemName}
                onChange={(e) => onUpdate(idx, { customItemName: e.target.value })}
                disabled={disabled}
                hint="Not in the catalog — Procurement will source this after approval."
              />
              <div style={{ marginTop: 10 }}>
                <Input
                  label="Unit"
                  placeholder="e.g. seat/year, license"
                  value={line.offCatalogUnit || ""}
                  error={errors.offCatalogUnit}
                  onChange={(e) => onUpdate(idx, { offCatalogUnit: e.target.value })}
                  disabled={disabled}
                />
              </div>
              <Button variant="ghost" size="sm" onClick={handleBackToCatalog} disabled={disabled} style={{ marginTop: 6 }}>
                Choose from catalog instead
              </Button>
            </div>
          ) : creatingNewItem ? (
            <div className="pc-hint" style={{ alignSelf: "center" }}>
              Creating a new catalog item below…
            </div>
          ) : (
            <MDSelect
              required
              labelType="non-sticky"
              labelText="Item"
              className="pc-md-select"
              // An already-saved line keeps showing its item even before a group is picked.
              options={needsGroupItem ? [] : itemOptions}
              value={
                itemOptions.find((o) => String(o.value) === String(line.itemId)) ||
                (line.itemId ? { value: line.itemId, label: line.itemName || `#${line.itemId}` } : null)
              }
              onChange={(o) => handleItemChange(o?.value ?? "")}
              isOptionDisabled={(o) => !!o.disabled}
              // The "+ add / create" escape hatches stay visible whatever is typed.
              filterOption={(o, input) =>
                !o.data.raw ||
                `${o.label} ${o.data.raw.itemCode || ""}`.toLowerCase().includes(input.trim().toLowerCase())
              }
              isDisabled={disabled || itemsLoading || needsGroupItem}
              placeholder={
                needsGroupItem ? "Chọn Group Item trước" : itemsLoading ? "Loading items…" : "Search item name or code…"
              }
            />
          )}
          {errors.referencePrice && (
            <span className="pc-hint pc-hint--error" role="alert">
              {errors.referencePrice}
            </span>
          )}
        </div>
        <div className="pc-lf pc-lf--sm">
          <Select
            label="Type"
            required
            allowEmpty={false}
            value={line.type}
            onChange={(v) => onUpdate(idx, { type: v })}
            options={ITEM_TYPE_OPTIONS}
            disabled={disabled}
          />
        </div>
        {line.type === "replacement" && (
          <div className="pc-lf">
            <Input
              label="Replaced Asset Code"
              required
              placeholder="e.g. AST-00234"
              value={line.replacedAssetCode}
              error={errors.replacedAssetCode}
              onChange={(e) => onUpdate(idx, { replacedAssetCode: e.target.value })}
              disabled={disabled}
            />
          </div>
        )}
        <div className="pc-lf pc-lf--sm">
          <NumberInput
            label={monthBreakdown ? "Quantity / month" : "Quantity"}
            required
            value={line.quantity}
            onChange={(v) => onUpdate(idx, { quantity: v })}
            disabled={disabled}
            hint={monthBreakdown ? "Applies to every month — adjust single months below." : undefined}
          />
        </div>
      </div>

      <div className="pc-line-row">
        <div className="pc-lf">
          <NumberInput
            label="Unit Price (VND)"
            required
            value={line.unitPrice}
            onChange={(v) => onUpdate(idx, { unitPrice: v })}
            disabled={disabled}
            hint={
              isEstimatePrice
                ? "Giá ước chừng — cần báo giá NCC. Editable."
                : "Defaults from the item's standard price — editable."
            }
          />
        </div>
        {showDeliveryContact && (
          <div className="pc-lf">
            <Input
              label="Delivery Contact Name"
              required
              placeholder="e.g. Nguyen Van A"
              value={line.deliveryContactName}
              error={errors.deliveryContactName}
              onChange={(e) => onUpdate(idx, { deliveryContactName: e.target.value })}
              disabled={disabled}
              hint="Tự điền theo người đại diện của center — có thể chỉnh."
            />
          </div>
        )}
        {showDeliveryContact && (
          <div className="pc-lf">
            <Input
              label="Delivery Contact Phone"
              required
              placeholder="e.g. 0901234567"
              value={line.deliveryContactPhone}
              error={errors.deliveryContactPhone}
              onChange={(e) => onUpdate(idx, { deliveryContactPhone: e.target.value })}
              disabled={disabled}
            />
          </div>
        )}
        {showDeliveryContact && (
          <div className="pc-lf pc-lf--md">
            <Input
              label="Delivery Address"
              placeholder="Địa chỉ giao hàng"
              maxLength={500}
              value={line.deliveryAddress || ""}
              error={errors.deliveryAddress}
              onChange={(e) => onUpdate(idx, { deliveryAddress: e.target.value })}
              disabled={disabled}
              hint="Tự điền theo địa chỉ của center — có thể chỉnh."
            />
          </div>
        )}
      </div>

      <div className="pc-line-row">
        <div className="pc-lf pc-lf--full">
          <Input
            label="Note"
            placeholder={overBudget ? "Reason for exceeding budget" : "Optional note for this line"}
            required={overBudget}
            value={line.note}
            error={errors.note}
            onChange={(e) => onUpdate(idx, { note: e.target.value })}
            disabled={disabled}
          />
        </div>
      </div>

      <div className="pc-line-row">
        <div className="pc-lf pc-lf--md">
          <Input
            label="Link to Ticket"
            required={overBudget}
            placeholder="e.g. https://helpdesk.company.com/TICKET-1234"
            value={line.ticketLink}
            error={errors.ticketLink}
            onChange={(e) => onUpdate(idx, { ticketLink: e.target.value })}
            disabled={disabled}
          />
        </div>
        {showProofImage && (
          <div className="pc-lf pc-lf--md">
            <FileSlot
              label="Additional Image Proof"
              file={line.proofImage}
              onSelect={handleProofImageSelect}
              disabled={disabled}
            />
          </div>
        )}
      </div>

      {creatingNewItem && (
        <div className="pc-line-newitem">
          <div className="pc-stat-label" style={{ marginBottom: 8 }}>
            New catalog item
          </div>
          <LineGrid min={160}>
            <Input
              label="Item Code"
              required
              value={newItemForm.itemCode}
              error={newItemErrors.itemCode}
              onChange={(e) => setNewItemField("itemCode", e.target.value)}
              disabled={creatingItemSaving}
            />
            <Input
              label="Name"
              required
              value={newItemForm.name}
              error={newItemErrors.name}
              onChange={(e) => setNewItemField("name", e.target.value)}
              disabled={creatingItemSaving}
            />
            <Select
              label="Group"
              required
              value={newItemForm.itemGroupId}
              onChange={(v) => setNewItemField("itemGroupId", v)}
              options={newItemGroupOptions}
              disabled={creatingItemSaving}
            />
            <Input
              label="UOM"
              required
              placeholder="e.g. Cái, seat/year"
              value={newItemForm.uom}
              error={newItemErrors.uom}
              onChange={(e) => setNewItemField("uom", e.target.value)}
              disabled={creatingItemSaving}
            />
            <Select
              label="Default Vendor"
              required
              value={newItemForm.defaultVendorId}
              onChange={(v) => setNewItemField("defaultVendorId", v)}
              options={newItemVendorOptions}
              disabled={creatingItemSaving}
            />
            <NumberInput
              label="Standard Price (VND)"
              required
              value={newItemForm.standardPrice}
              onChange={(v) => setNewItemField("standardPrice", v)}
              disabled={creatingItemSaving}
            />
          </LineGrid>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <Button
              variant="primary"
              size="sm"
              disabled={creatingItemSaving || !isNewItemFormValid}
              onClick={handleSaveNewItem}
            >
              {creatingItemSaving ? "Creating…" : "Create item"}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleCancelNewItem} disabled={creatingItemSaving}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {showProofImage && (
        <p className="pc-hint pc-line-hint">
          Add a ticket link or a photo proof for this item — either one gives Procurement what
          they need to act right away. Skip both, and they may have to circle back for more
          details, which only slows your order down.
        </p>
      )}

      {monthBreakdown?.length > 0 && (
        <table className="pc-table" style={{ marginTop: 8 }}>
          <thead>
            <tr>
              <th>Month</th>
              <th className="pc-th-right">Quantity</th>
              <th className="pc-th-right">Unit price</th>
              <th className="pc-th-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {monthBreakdown.map((m) => (
              <tr key={m.key}>
                <td>{m.label}</td>
                <td className="pc-td-right">
                  <NumberControl
                    className="pc-control"
                    style={{ width: 90, textAlign: "right", marginLeft: "auto" }}
                    aria-label={`Quantity for ${m.label}`}
                    aria-invalid={!(Number(m.quantity) > 0) || undefined}
                    value={m.quantity}
                    disabled={disabled}
                    onChange={(v) => onMonthQuantity(idx, m.key, v)}
                  />
                </td>
                <td className="pc-td-right">
                  <Currency value={m.unitPrice} />
                </td>
                <td className="pc-td-right">
                  <Currency value={m.amount} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {errors.monthQuantity && (
        <span className="pc-hint pc-hint--error" role="alert">
          {errors.monthQuantity}
        </span>
      )}

      <div className="pc-line-foot">
        <div className="pc-line-foot-l">
          {showDeliveryContact && (
            <Checkbox
              checked={!!line.showToCenter}
              onChange={(v) => onUpdate(idx, { showToCenter: v })}
              label="Hiển thị order này cho staff của center"
              disabled={disabled || !line.centerId}
            />
          )}
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<PackageSearch aria-hidden />}
            disabled={disabled || !line.itemId || !line.centerId || stockResult.isFetching}
            onClick={handleCheckStock}
          >
            {stockResult.isFetching ? "Checking…" : "Check Stock"}
          </Button>
          {line.stockReading != null && (
            <span className="pc-hint">Current stock: {line.stockReading}</span>
          )}
        </div>
        {canRemove && (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Trash2 aria-hidden />}
            onClick={() => onRemove(idx)}
            disabled={disabled}
            style={{ color: "var(--pc-danger)" }}
          >
            Remove
          </Button>
        )}
      </div>
    </div>
  )
}

export default OrderLineRow
